/* eslint-disable array-callback-return */
import type {
  FileSource,
  GalleryData_Item_Image,
  GalleryData_Item_Video,
  Node,
  RichContent,
} from './../../ricos-schema/legacy/wix/rich_content/v1';
import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';

export type ImageMedia = {
  type: 'image';
  url?: string;
  width?: number;
  height?: number;
  alt?: string;
  caption?: string;
};

export type VideoMedia = {
  type: 'video';
  url?: string;
  thumbnailUrl?: string;
  uploadDate?: string;
  width?: number;
  height?: number;
  title?: string;
  duration?: number;
};

const findAllMediaNodes = (root: RichContent) => {
  const types = [Node_Type.IMAGE, Node_Type.VIDEO, Node_Type.GALLERY, Node_Type.GIF];
  const nodes = root.nodes ?? [];
  const result: Node[] = [];
  const stack = [...nodes];

  while (stack.length) {
    const node = stack.pop();
    if (!node) {
      continue;
    }

    if (types.includes(node.type)) {
      result.push(node);
    }
    if (node.nodes) {
      stack.push(...node.nodes);
    }
  }

  return result;
};

const extractUrl = (
  source: FileSource | undefined,
  type: 'media' | 'video',
  urlResolver?: MediaUrlResolvers[keyof MediaUrlResolvers]
) => {
  if (!source) {
    return;
  }

  const { url, id } = source;
  try {
    if (url) {
      /**
       * Here we are checking if the URL is actually valid
       * We need it because gallery saves the IDs as URLs
       */
      // eslint-disable-next-line
      new URL(url);
      return url;
    }
  } catch (e) {}

  const sourceId = id || url;

  if (sourceId) {
    const subdomain = type === 'media' ? 'static' : 'video';

    return urlResolver
      ? urlResolver(sourceId)
      : `https://${subdomain}.wixstatic.com/${type}/${sourceId.replace(`${type}/`, '')}`;
  }
};

const extractImageUrl = (image: FileSource | undefined, urlResolver?: MediaUrlResolvers['image']) =>
  extractUrl(image, 'media', urlResolver);
const extractVideoUrl = (video: FileSource | undefined, urlResolver?: MediaUrlResolvers['video']) =>
  extractUrl(video, 'video', urlResolver);

const imageNodeToImageMediaConverter = (
  node: Node,
  urlResolver?: MediaUrlResolvers['image']
): ImageMedia | undefined => {
  if (node.type !== Node_Type.IMAGE) {
    return;
  }

  return {
    type: 'image',
    url: extractImageUrl(node.imageData?.image?.src, urlResolver),
    width: node.imageData?.image?.width,
    height: node.imageData?.image?.height,
    alt: node.imageData?.altText,
    caption: node.imageData?.caption,
  };
};

const gifNodeToVideoMediaConverter = (gifData: NonNullable<Node['gifData']>): VideoMedia => {
  return {
    type: 'video',
    url: gifData.original?.mp4 ?? gifData.downsized?.mp4,
    thumbnailUrl: gifData.original?.still,
    width: gifData.width,
    height: gifData.height,
  };
};

const gifNodeToImageMediaConverter = (gifData: NonNullable<Node['gifData']>): ImageMedia => {
  return {
    type: 'image',
    url: gifData.original?.gif ?? gifData.downsized?.gif,
    width: gifData.width,
    height: gifData.height,
  };
};

const gifNodeToMediaConverter = (node: Node): ImageMedia | VideoMedia | undefined => {
  if (node.type !== Node_Type.GIF) {
    return;
  }
  if (node.gifData?.original?.mp4 || node.gifData?.downsized?.mp4) {
    return gifNodeToVideoMediaConverter(node.gifData);
  }
  if (node.gifData?.original?.gif || node.gifData?.downsized?.gif) {
    return gifNodeToImageMediaConverter(node.gifData);
  }
};

const videoNodeToMediaConverter = (
  node: Node,
  urlResolvers?: { video?: MediaUrlResolvers['video']; thumbnail?: MediaUrlResolvers['thumbnail'] }
): VideoMedia | undefined => {
  if (node.type !== Node_Type.VIDEO) {
    return;
  }
  return {
    type: 'video',
    url: extractVideoUrl(node.videoData?.video?.src, urlResolvers?.video),
    thumbnailUrl: extractImageUrl(node.videoData?.thumbnail?.src, urlResolvers?.thumbnail),
    width: node.videoData?.video?.width,
    height: node.videoData?.video?.height,
    duration: node.videoData?.video?.duration,
    title: node.videoData?.title,
  };
};

const galleryImageToMediaConverter = (
  image: GalleryData_Item_Image,
  urlResolver?: MediaUrlResolvers['image']
): ImageMedia => {
  return {
    type: 'image',
    url: extractImageUrl(image?.media?.src, urlResolver),
    width: image?.media?.width,
    height: image?.media?.height,
  };
};

const galleryVideoToMediaConverter = (
  video: GalleryData_Item_Video,
  urlResolvers?: { video?: MediaUrlResolvers['video']; thumbnail?: MediaUrlResolvers['thumbnail'] }
): VideoMedia => {
  return {
    type: 'video',
    url: extractVideoUrl(video?.media?.src, urlResolvers?.video),
    width: video?.media?.width,
    height: video?.media?.height,
    duration: video?.media?.duration,
    thumbnailUrl: extractImageUrl(video?.thumbnail?.src, urlResolvers?.thumbnail),
  };
};

const galleryNodeToMediaConverter = (
  node: Node,
  urlResolvers?: MediaUrlResolvers
): (ImageMedia | VideoMedia)[] => {
  if (node.type !== Node_Type.GALLERY) {
    return [];
  }

  const items = node.galleryData?.items || [];

  return items
    .map(item => {
      if (item.image) {
        return galleryImageToMediaConverter(item.image, urlResolvers?.image);
      }
      if (item.video) {
        return galleryVideoToMediaConverter(item.video, urlResolvers);
      }
    })
    .filter(Boolean) as (ImageMedia | VideoMedia)[];
};

type UrlResolver = (id: string) => string;

type MediaUrlResolvers = {
  image?: UrlResolver;
  video?: UrlResolver;
  thumbnail?: UrlResolver;
};

export const getMediaData = (content: RichContent, mediaUrlResolvers?: MediaUrlResolvers) => {
  const allNodes = findAllMediaNodes(content);

  return allNodes
    .map(node => {
      switch (node.type) {
        case Node_Type.IMAGE:
          return imageNodeToImageMediaConverter(node, mediaUrlResolvers?.image);
        case Node_Type.GIF:
          return gifNodeToMediaConverter(node);
        case Node_Type.VIDEO:
          return videoNodeToMediaConverter(node, mediaUrlResolvers);
        case Node_Type.GALLERY:
          return galleryNodeToMediaConverter(node, mediaUrlResolvers);
        default:
          return undefined;
      }
    })
    .flat()
    .filter(Boolean) as (ImageMedia | VideoMedia)[];
};
